"use client";

import { HyperliquidConnectionStatus } from "@cede/types";
import { postHyperliquidInfo } from "@cede/utils";
import {
  type HyperliquidConnectionMode,
  useHyperliquidConnection,
} from "@/hooks/trade/hyperliquid/useHyperliquidConnection";
import { useQuery } from "@tanstack/react-query";

interface ExtraAgent {
  address: string;
  name: string;
  validUntil: number;
}

/**
 * Validates that the stored agent wallet is still approved on Hyperliquid.
 *
 * Uses the public `extraAgents` endpoint (unsigned, no auth needed) to fetch
 * the list of approved agent wallets for the connected main wallet, then checks
 * if the stored agent address is present.
 *
 * Returns `isAgentValid: false` when the agent is stale (revoked/expired),
 * signaling the UI to show a reconnect prompt.
 */
export function useAgentWalletValidation(options?: { mode?: HyperliquidConnectionMode }) {
  const { mode = "default" } = options ?? {};
  const { agentData, hasAgentData, connectedAddress } = useHyperliquidConnection({ mode });

  const { data: isAgentValid, isLoading: isChecking } = useQuery({
    queryKey: ["agent-wallet-validation", mode, connectedAddress, agentData.agentAddress],
    queryFn: async () => {
      const agents = await postHyperliquidInfo<ExtraAgent[]>({
        type: "extraAgents",
        user: connectedAddress!,
      });
      return agents.some((a) => a.address.toLowerCase() === agentData.agentAddress.toLowerCase());
    },
    enabled: Boolean(
      connectedAddress &&
      hasAgentData &&
      agentData.agentAddress &&
      agentData.status !== HyperliquidConnectionStatus.WAITING_APPROVAL,
    ),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    isAgentValid: isAgentValid ?? true,
    isChecking,
  };
}
